import React, { memo, useEffect, useState, useCallback } from "react";
import { Box, Typography, Chip, Tooltip } from "@mui/material";
import { Code as VariablesIcon, Save as SaveIcon } from "@mui/icons-material";
import AerosNode from "./AerosNode";
import { TONES, nodeSummary } from "../flowRules";
import { processVariablesInText } from "../../../utils/variableUtils";
import { i18n } from "../../../translate/i18n";

const logic = TONES.logic;
const chipSx = { height: 20, fontSize: "0.75rem", bgcolor: logic.bg, color: logic.color, "& .MuiChip-label": { px: 1 } };
const boxSx = { bgcolor: "action.hover", p: 1, borderRadius: 2, overflow: "auto", maxHeight: 150, wordBreak: "break-word" };

export default memo(({ data, isConnectable, id, selected }) => {
  const [processedQuestion, setProcessedQuestion] = useState(data.question || "");
  const [variableValue, setVariableValue] = useState("");
  const [isExecutionMode, setIsExecutionMode] = useState(false);

  const extractVariables = (text) => {
    if (!text) return [];
    const regex = /\$\{([^}]+)\}/g;
    const matches = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      matches.push(match[1]);
    }
    return [...new Set(matches)];
  };

  const updateProcessedQuestion = useCallback(() => {
    setProcessedQuestion(processVariablesInText(data.question || ""));
  }, [data.question]);

  useEffect(() => {
    const isExecutionPath = !window.location.pathname.includes("/flowbuilder-config");
    setIsExecutionMode(isExecutionPath);
    if (isExecutionPath && data.variableName) {
      const checkVariableValue = () => {
        if (window.flowVariables && window.flowVariables[data.variableName]) {
          setVariableValue(window.flowVariables[data.variableName]);
        }
      };
      checkVariableValue();
      const intervalId = setInterval(checkVariableValue, 1000);
      return () => clearInterval(intervalId);
    }
  }, [data.variableName]);

  useEffect(() => {
    updateProcessedQuestion();
    window.addEventListener("flowVariableUpdate", updateProcessedQuestion);
    const intervalId = setInterval(updateProcessedQuestion, 500);
    return () => {
      window.removeEventListener("flowVariableUpdate", updateProcessedQuestion);
      clearInterval(intervalId);
    };
  }, [data.question, updateProcessedQuestion]);

  return (
    <AerosNode
      id={id}
      type="input"
      selected={selected}
      isConnectable={isConnectable}
      width={260}
      extra={
        <div className="nodrag" style={{ padding: "0 12px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
          <Box sx={boxSx}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              {i18n.t("aeros.flow.input.question")}
            </Typography>
            {data.question ? (
              !isExecutionMode ? (
                data.question.split(/\$\{([^}]+)\}/).map((part, index) =>
                  index % 2 === 0 ? (
                    <Typography key={index} variant="body2" component="span">{part}</Typography>
                  ) : (
                    <Chip key={index} label={part} size="small" sx={{ ...chipSx, my: 0.25, mx: 0.5 }} />
                  )
                )
              ) : (
                <Typography variant="body2">{processedQuestion}</Typography>
              )
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                {i18n.t("aeros.flow.input.noQuestion")}
              </Typography>
            )}
          </Box>

          <Box sx={boxSx}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              {i18n.t("aeros.flow.input.variable")}
            </Typography>
            {data.variableName ? (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Chip label={data.variableName} size="small" sx={{ ...chipSx, height: 24, fontSize: "0.85rem" }} />
                {isExecutionMode && variableValue && (
                  <Tooltip title={i18n.t("aeros.flow.input.value", { value: variableValue })}>
                    <Box sx={{ ml: 1.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <SaveIcon sx={{ color: logic.color, width: 16, height: 16 }} />
                      <Typography variant="caption" color="text.primary">
                        {variableValue.length > 15 ? `${variableValue.substring(0, 15)}...` : variableValue}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                {i18n.t("aeros.flow.input.noVariable")}
              </Typography>
            )}
          </Box>

          {data.question && data.question.includes("${") && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <VariablesIcon sx={{ color: logic.color, width: 16, height: 16 }} />
              <Typography variant="caption" sx={{ color: logic.color }}>
                {i18n.t("aeros.flow.input.usingVariables", { names: extractVariables(data.question).join(", ") })}
              </Typography>
            </Box>
          )}
        </div>
      }
    >
      {nodeSummary({ type: "input", data })}
    </AerosNode>
  );
});
